import {useMemo} from "react";
import {useSelector} from "react-redux";
import {useMutation, useQuery, useQueryClient} from "react-query";
import {AgeCategory} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {Limit} from "crqlar-react-common/dist/models/common/limit";
import {User} from "crqlar-react-common/dist/models/identity/user.model";
import {Printout, PrintoutType} from "crqlar-react-common/dist/models/printout/printout.model";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {Reservation, ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {ResourceCategory} from "crqlar-react-common/dist/models/resourceCategory/resourceCategory.model";
import {Rate} from "crqlar-react-common/dist/models/rate/rate.model";
import {TaxRate} from "crqlar-react-common/dist/models/rate/taxRate.model";
import {Sector, SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {ServiceCategory} from "crqlar-react-common/dist/models/serviceCategory/serviceCategory.model";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {getPrimaryProfileId} from "crqlar-react-common/dist/utils/reservationProfile.util";
import {AgeCategoryService} from "crqlar-react-common/dist/services/ageCategory/ageCategory.service";
import {PrintoutService} from "crqlar-react-common/dist/services/printout/printout.service";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {RateService} from "crqlar-react-common/dist/services/rate/rate.service";
import {TaxRateService} from "crqlar-react-common/dist/services/rate/taxRate.service";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {ResourceCategoryService} from "crqlar-react-common/dist/services/resourceCategory/resourceCategory.service";
import {ResourceService} from "crqlar-react-common/dist/services/resource/resource.service";
import {SectorService} from "crqlar-react-common/dist/services/sector/sector.service";
import {ServiceCategoryService} from "crqlar-react-common/dist/services/serviceCategory/serviceCategory.service";
import {ServiceService} from "crqlar-react-common/dist/services/service/service.service";
import {printoutHasLanguage, printoutHasLayouts, resolvePrintoutLanguage} from "../../../core/printoutDocument";
import {PrintoutVariableResolveContext} from "../../../variables/printoutVariableResolver";
import {
    getLinkedReservations,
    getReservationMealPeriodRows,
    getReservationResources,
    type ReservationMealPeriodPrintRow,
} from "./reservationPrintData";

interface ReservationPrintRelatedData {
    ageCategories: AgeCategory[];
    linkedReservations: Reservation[];
    profile?: Profile;
    profiles: Profile[];
    reservationMealPeriods: ReservationMealPeriodPrintRow[];
    reservationResources: Resource[];
    restaurantResource?: Resource;
    resourceCategories: ResourceCategory[];
    resources: Resource[];
    sector?: Sector;
    service?: Service;
    serviceCategories: ServiceCategory[];
    taxRates: TaxRate[];
}

type ResolveRecord = Record<string, unknown>;

export interface ReservationPrintData {
    ageCategories: AgeCategory[];
    context?: PrintoutVariableResolveContext;
    errorMessage?: string;
    isLoading: boolean;
    language?: string;
    profile?: Profile;
    reservation?: Reservation;
    reservationResources: Resource[];
    restaurantResource?: Resource;
    resourceCategories: ResourceCategory[];
    resources: Resource[];
    sector?: Sector;
    selectedPrintout?: Printout;
    service?: Service;
    serviceCategories: ServiceCategory[];
    taxRates: TaxRate[];
    printouts: Printout[];
    /** Saves the built-in default for this reservation as a real printout. Absent when none fits. */
    createDefaultPrintout?: () => void;
    creatingDefaultPrintout: boolean;
    createDefaultPrintoutError?: string;
}

const LIST_LIMIT: Limit = {
    offset: 0,
    limit: 1000,
    sort: [{field: "name", direction: "ASC"}],
};

const PRINTOUT_LIST_LIMIT: Limit = {
    offset: 0,
    limit: 1000,
    sort: [{field: "name", direction: "ASC"}],
};

const RESERVATION_PRINTOUT_TYPES = new Set<PrintoutType>([
    PrintoutType.RESERVATION_CONFIRMATION,
    PrintoutType.RESERVATION_CANCELLATION,
]);

const getErrorMessage = (error: unknown) => {
    if (!error) return undefined;
    if (error instanceof Error) return error.message;
    return "Unable to load print preview data.";
};

const uniqueIds = (ids: Array<string | undefined>) => Array.from(new Set(
    ids.filter((id): id is string => Boolean(id)),
));

const getRelatedReservationPrintData = async (reservation: Reservation): Promise<ReservationPrintRelatedData> => {
    const profileId = getPrimaryProfileId(reservation);
    const otherProfileIds = uniqueIds(reservation.profileIds ?? []).filter(id => id !== profileId);
    const linkedReservations = await getLinkedReservations(reservation);
    const resourceIds = uniqueIds(linkedReservations.flatMap(linkedReservation => linkedReservation.resourceIds ?? []));

    const [
        ageCategories,
        profile,
        otherProfiles,
        resources,
        sector,
        service,
    ] = await Promise.all([
        AgeCategoryService.getAllAgeCategories({
            limit: LIST_LIMIT,
        }).then(page => page.content ?? []),
        profileId
            ? ProfileService.getProfileById(profileId, true)
            : Promise.resolve(undefined),
        otherProfileIds.length
            ? ProfileService.getAllProfiles({
                id: otherProfileIds,
                followMerge: true,
                limit: {offset: 0, limit: otherProfileIds.length},
            }).then(page => page.content ?? [])
            : Promise.resolve([]),
        resourceIds.length
            ? ResourceService.getAllResources({
                id: resourceIds,
                limit: LIST_LIMIT,
            }).then(page => page.content ?? [])
            : Promise.resolve([]),
        reservation.sectorId
            ? SectorService.getSectorById(reservation.sectorId)
            : Promise.resolve(undefined),
        reservation.serviceId
            ? ServiceService.getServiceById(reservation.serviceId)
            : Promise.resolve(undefined),
    ]);
    const missingParentIds = uniqueIds(
        resources
            .map(resource => resource.parentId)
            .filter(parentId => !resources.some(resource => resource.id === parentId)),
    );
    const parentResources = missingParentIds.length && sector?.sectorType === SectorType.RESTAURANT
        ? await ResourceService.getAllResources({
            id: missingParentIds,
            limit: LIST_LIMIT,
        }).then(page => page.content ?? [])
        : [];
    const allResources = [
        ...resources,
        ...parentResources,
    ];
    const {
        reservationResources,
        restaurantResource,
    } = getReservationResources(reservation, allResources, sector);
    const reservationMealPeriods = getReservationMealPeriodRows(linkedReservations, allResources, sector);
    const serviceCategoryIds = uniqueIds(service?.categoryIds ?? []);
    const serviceRateIds = uniqueIds(service?.rateIds ?? []);
    const resourceCategoryIds = uniqueIds(allResources.flatMap(resource => resource.categoryIds ?? []));

    const [
        serviceCategories,
        resourceCategories,
        serviceRates,
    ] = await Promise.all([
        serviceCategoryIds.length
            ? ServiceCategoryService.getAllServiceCategories({
                id: serviceCategoryIds,
                limit: LIST_LIMIT,
            }).then(page => page.content ?? [])
            : Promise.resolve([]),
        resourceCategoryIds.length
            ? ResourceCategoryService.getAllResourceCategories({
                id: resourceCategoryIds,
                limit: LIST_LIMIT,
            }).then(page => page.content ?? [])
            : Promise.resolve([]),
        serviceRateIds.length
            ? RateService.getAllRates({
                id: serviceRateIds,
                limit: LIST_LIMIT,
            }).then(page => page.content ?? [])
            : Promise.resolve([]),
    ]);
    const expandedServiceRates = service?.ratesExpanded?.length
        ? service.ratesExpanded
        : serviceRates as Rate[];
    const taxRateIds = uniqueIds(expandedServiceRates
        .filter(rate => !rate.taxRateSnap)
        .map(rate => rate.taxRateId));
    const taxRates = taxRateIds.length
        ? await TaxRateService.getAllTaxRates({
            id: taxRateIds,
            limit: LIST_LIMIT,
        }).then(page => page.content ?? [])
        : [];
    const taxRatesById = new Map(taxRates
        .filter(taxRate => Boolean(taxRate.id))
        .map(taxRate => [taxRate.id!, taxRate]));
    const serviceRatesWithTaxRates = expandedServiceRates.map(rate => {
        if (rate.taxRateSnap || !rate.taxRateId) return rate;

        return {
            ...rate,
            taxRateSnap: taxRatesById.get(rate.taxRateId),
        };
    });
    const serviceWithRates = service
        ? {
            ...service,
            ratesExpanded: serviceRatesWithTaxRates,
        }
        : undefined;

    // Followed merges can return the same guest under several original profile ids.
    const profiles = Array.from(new Map(
        [profile, ...otherProfiles]
            .filter((guest): guest is Profile => Boolean(guest))
            .map(guest => [guest.id, guest]),
    ).values());

    return {
        ageCategories,
        linkedReservations,
        profile,
        profiles,
        resourceCategories,
        reservationMealPeriods,
        reservationResources,
        restaurantResource,
        resources: allResources,
        sector,
        service: serviceWithRates,
        serviceCategories,
        taxRates,
    };
};

const getReservationPrintoutType = (reservation: Reservation) => (
    reservation.status === ReservationStatus.CANCELLED
        ? PrintoutType.RESERVATION_CANCELLATION
        : PrintoutType.RESERVATION_CONFIRMATION
);

const printoutMatchesReservation = (printout: Printout, reservation: Reservation) => {
    if (!printoutHasLayouts(printout)) return false;
    if (printout.type !== getReservationPrintoutType(reservation)) return false;
    if (
        printout.scopedSectorIds?.length
        && (!reservation.sectorId || !printout.scopedSectorIds.includes(reservation.sectorId))
    ) return false;

    return true;
};

const getPrintoutScore = (
    printout: Printout,
    reservation: Reservation,
    profile?: Profile,
) => {
    let score = 1;
    if (printoutHasLanguage(printout, profile?.languageCode)) score += 100;
    if (reservation.sectorId && printout.scopedSectorIds?.includes(reservation.sectorId)) score += 20;
    if (!printout.scopedSectorIds?.length) score += 5;

    return score;
};

export const selectReservationPrintout = (
    printouts: Printout[],
    reservation?: Reservation,
    profile?: Profile,
) => {
    if (!reservation) return undefined;

    return printouts
        .filter(printout => printoutMatchesReservation(printout, reservation))
        .sort((left, right) => {
            const scoreDifference = getPrintoutScore(right, reservation, profile)
                - getPrintoutScore(left, reservation, profile);
            if (scoreDifference !== 0) return scoreDifference;
            return (left.name ?? "").localeCompare(right.name ?? "");
        })[0];
};

export const useReservationPrintData = (
    reservationId?: string,
    selectedPrintoutId?: string,
    requestedLanguage?: string,
): ReservationPrintData => {
    const organization = useOrganization();
    const user = useSelector<any, User | undefined>(state => state.auth.user);

    const queryClient = useQueryClient();

    const reservationQuery = useQuery(
        ["printV3", "reservation", reservationId],
        () => ReservationService.getReservationById(reservationId!),
        {
            enabled: Boolean(reservationId),
            refetchOnWindowFocus: false,
        },
    );

    const relatedDataQuery = useQuery(
        ["printV3", "reservationRelatedData", reservationQuery.data?.id],
        () => getRelatedReservationPrintData(reservationQuery.data!),
        {
            enabled: Boolean(reservationQuery.data),
            refetchOnWindowFocus: false,
        },
    );

    const printoutQuery = useQuery(
        ["printV3", "reservationPrintouts"],
        () => PrintoutService.getAllPrintouts({
            archived: false,
            limit: PRINTOUT_LIST_LIMIT,
        }).then(page => (
            page.content?.filter(printout => (
                printout.type !== undefined && RESERVATION_PRINTOUT_TYPES.has(printout.type)
            )) ?? []
        )),
        {refetchOnWindowFocus: false},
    );

    const templateQuery = useQuery(
        ["printV3", "printoutTemplates"],
        () => PrintoutService.getPrintoutTemplates(),
        {refetchOnWindowFocus: false},
    );

    /*
        The template this reservation would get if the property had no printout of its own: its
        sector's built-in one where that exists, the generic OTHER one otherwise, and the
        cancellation template for a cancelled reservation.

        Only the choice happens here. Nothing renders a built-in document as though it were the
        property's own - that is what makes "template" and "live printout" impossible to tell apart
        - so it stays a template until the empty state's button has the backend create it, after
        which it is an ordinary printout that appears in the list and opens in the editor.
    */
    const defaultTemplate = useMemo(() => {
        const reservation = reservationQuery.data;
        if (!reservation) return undefined;

        const type = getReservationPrintoutType(reservation);
        const candidates = (templateQuery.data ?? []).filter(template => template.type === type);

        return candidates.find(entry => entry.scope === relatedDataQuery.data?.sector?.sectorType)
            ?? candidates.find(entry => entry.scope === SectorType.OTHER)
            ?? candidates.find(entry => !entry.scope);
    }, [
        relatedDataQuery.data?.sector?.sectorType,
        reservationQuery.data,
        templateQuery.data,
    ]);

    const matchingPrintouts = useMemo(
        () => {
            const printouts = printoutQuery.data ?? [];
            if (!reservationQuery.data) return printouts;

            return printouts.filter(printout => printoutMatchesReservation(printout, reservationQuery.data!));
        },
        [
            reservationQuery.data,
            printoutQuery.data,
        ],
    );

    const selectedPrintout = useMemo(
        () => (
            matchingPrintouts.find(printout => printout.id === selectedPrintoutId)
            ?? selectReservationPrintout(
                matchingPrintouts,
                reservationQuery.data,
                relatedDataQuery.data?.profile,
            )
        ),
        [
            matchingPrintouts,
            relatedDataQuery.data?.profile,
            reservationQuery.data,
            selectedPrintoutId,
        ],
    );
    const language = selectedPrintout
        ? resolvePrintoutLanguage(
            selectedPrintout,
            requestedLanguage ?? relatedDataQuery.data?.profile?.languageCode,
        )
        : undefined;

    const context = useMemo<PrintoutVariableResolveContext | undefined>(() => {
        if (!reservationQuery.data || !selectedPrintout || !language) return undefined;

        const relatedData = relatedDataQuery.data;

        return {
            ageCategories: relatedData?.ageCategories as unknown as ResolveRecord[] | undefined,
            language,
            now: new Date(),
            organization: organization as unknown as ResolveRecord | undefined,
            profile: relatedData?.profile as unknown as ResolveRecord | undefined,
            reservation: reservationQuery.data as unknown as ResolveRecord,
            reservationProfiles: relatedData?.profiles as unknown as ResolveRecord[] | undefined,
            reservationMealPeriods: relatedData?.reservationMealPeriods as unknown as ResolveRecord[] | undefined,
            reservations: relatedData?.linkedReservations as unknown as ResolveRecord[] | undefined,
            resource: relatedData?.reservationResources[0] as unknown as ResolveRecord | undefined,
            resourceCategories: (relatedData?.resourceCategories ?? []) as unknown as ResolveRecord[],
            resourceCategory: relatedData?.resourceCategories[0] as unknown as ResolveRecord | undefined,
            resources: (relatedData?.resources ?? []) as unknown as ResolveRecord[],
            restaurant: relatedData?.restaurantResource as unknown as ResolveRecord | undefined,
            sector: relatedData?.sector as unknown as ResolveRecord | undefined,
            sectorResources: (relatedData?.reservationResources ?? []) as unknown as ResolveRecord[],
            service: relatedData?.service as unknown as ResolveRecord | undefined,
            serviceCategories: (relatedData?.serviceCategories ?? []) as unknown as ResolveRecord[],
            serviceCategory: relatedData?.serviceCategories[0] as unknown as ResolveRecord | undefined,
            taxRates: (relatedData?.taxRates ?? []) as unknown as ResolveRecord[],
            user: user as unknown as ResolveRecord | undefined,
        };
    }, [
        organization,
        language,
        relatedDataQuery.data,
        reservationQuery.data,
        selectedPrintout,
        user,
    ]);

    /*
        Creating from the template makes it the property's own printout: it lands in the Printouts
        list, opens in the editor, and is picked up here by the same match the list-driven path
        uses, so the preview switches from the empty state to the new document once the list
        refetches.
    */
    const createDefaultPrintout = useMutation(
        () => PrintoutService.createPrintoutFromTemplate({
            templateId: defaultTemplate!.id,
            language: requestedLanguage ?? relatedDataQuery.data?.profile?.languageCode,
            // A sector-specific template is saved scoped to the sector it was chosen for, narrower
            // than the backend's default of every sector of that type. Widening it is one edit in
            // the editor.
            scopedSectorIds: defaultTemplate!.scope && relatedDataQuery.data?.sector?.id
                ? [relatedDataQuery.data.sector.id]
                : undefined,
        }),
        {onSuccess: () => queryClient.invalidateQueries(["printV3", "reservationPrintouts"])},
    );

    const errorMessage = getErrorMessage(reservationQuery.error)
        ?? getErrorMessage(relatedDataQuery.error)
        ?? getErrorMessage(printoutQuery.error);

    return {
        ageCategories: relatedDataQuery.data?.ageCategories ?? [],
        context,
        errorMessage,
        isLoading: reservationQuery.isLoading || relatedDataQuery.isLoading
            || printoutQuery.isLoading || templateQuery.isLoading,
        language,
        profile: relatedDataQuery.data?.profile,
        reservation: reservationQuery.data,
        reservationResources: relatedDataQuery.data?.reservationResources ?? [],
        restaurantResource: relatedDataQuery.data?.restaurantResource,
        resourceCategories: relatedDataQuery.data?.resourceCategories ?? [],
        resources: relatedDataQuery.data?.resources ?? [],
        sector: relatedDataQuery.data?.sector,
        selectedPrintout,
        service: relatedDataQuery.data?.service,
        serviceCategories: relatedDataQuery.data?.serviceCategories ?? [],
        taxRates: relatedDataQuery.data?.taxRates ?? [],
        printouts: matchingPrintouts,
        createDefaultPrintout: defaultTemplate ? () => createDefaultPrintout.mutate() : undefined,
        creatingDefaultPrintout: createDefaultPrintout.isLoading,
        createDefaultPrintoutError: getErrorMessage(createDefaultPrintout.error),
    };
};
